import type { LensCardData } from '~/types/leLens';

import Core from './core';
import type { FlashCampaign, FlashDaily, FlashSnapshot } from './types';

type Row = Record<string, any>;
export const LIVE_CARDS = ['schedules', 'daily', 'markets', 'dimensions', 'identities', 'freshness'];
export function evidenceRows(cards: LensCardData[], id: string): Row[] {
  const card = cards.find((c) => c.cardId === id);
  if (!card || !['ready', 'empty'].includes(card.status))
    throw Error(card?.message || `${id} evidence is still loading.`);
  if (card.truncated)
    throw Error(`${id} evidence exceeds its row limit. Figures are withheld to prevent a partial benchmark.`);
  return (card.rows || []).flatMap((row) => {
    const parsed: unknown = JSON.parse(String(row.payload));
    if (!Array.isArray(parsed) || parsed.length !== Number(row.record_count))
      throw Error(`Invalid ${id} evidence batch.`);
    return parsed as Row[];
  });
}
const add = (date: string, n: number) =>
  new Date(Date.parse(date + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);
const one = (values: unknown, missing: string, conflict: string) => {
  const unique = [...new Set((Array.isArray(values) ? values : []).filter(Boolean).map(String))];
  return unique.length === 1 ? unique[0] : unique.length ? conflict : missing;
};
export function liveBenchmark(cards: LensCardData[], asOf: string): FlashSnapshot {
  const tables = Object.fromEntries(LIVE_CARDS.map((id) => [id, evidenceRows(cards, id)]));
  const freshness: Record<string, string> = Object.fromEntries(tables.freshness.map((r) => [r.source, r.fact_date]));
  if (['impressions', 'views', 'sales'].some((k) => !/^\d{4}-\d{2}-\d{2}$/.test(freshness[k] || '')))
    throw Error('All three fact freshness dates are required.');
  const cutoff = [add(asOf, -1), ...Object.values(freshness)].sort()[0];
  const campaigns: FlashCampaign[] = [];
  const schedules = tables.schedules.sort(
    (a, b) =>
      a.offer_bk.localeCompare(b.offer_bk) ||
      a.start_date.localeCompare(b.start_date) ||
      a.end_date.localeCompare(b.end_date) ||
      a.id.localeCompare(b.id),
  );
  for (const s of schedules) {
    if (
      s.product_bk !== 'hotel' ||
      s.model !== 'FLASH' ||
      !['lenewoffer', 'lereopen'].includes(
        String(s.dynamic_type)
          .toLowerCase()
          .replace(/[^a-z]/g, ''),
      ) ||
      s.start_date < '2026-01-01' ||
      s.end_date < s.start_date
    )
      continue;
    // Preserve the producer's fail-closed fact alias boundary.
    if (
      !Array.isArray(s.product_bookable_offer_bks) ||
      s.product_bookable_offer_bks.length !== 1 ||
      s.product_bookable_offer_bks[0] !== s.offer_bk
    )
      throw Error(`Hotel fact aliases require per-source validation: ${s.offer_bk}.`);
    const previous = campaigns[campaigns.length - 1];
    if (previous?.offerBk === s.offer_bk && s.start_date <= add(previous.endDate, 1)) {
      previous.endDate = [previous.endDate, s.end_date].sort()[1];
      continue;
    }
    campaigns.push({
      id: s.offer_bk + ':' + s.start_date,
      offerBk: s.offer_bk,
      productBk: 'hotel',
      productMapping: {
        basis: s.product_mapping_basis,
        bookableOfferBks: s.product_bookable_offer_bks,
        productValues: s.product_values,
      },
      name: s.vendor_name || s.name,
      opportunityName: s.name,
      country: s.country || 'Unknown country',
      destination: s.destination || 'Unknown destination',
      owner: s.owner || 'Unassigned',
      campaignType: s.campaign_type || 'Unknown campaign type',
      startDate: s.start_date,
      endDate: s.end_date,
      observationEnd: '',
      daysObserved: 0,
      durationDays: 0,
      status: '',
      isMature: false,
      daily: [],
      dailyByMarket: {},
      totals: { impressions: null, views: null, packages: null, margin: null },
      metrics: {},
    });
  }
  const byOffer = (rows: Row[]) => {
    const map = new Map<string, Row[]>();
    rows.forEach((row) => {
      const items = map.get(row.offer_bk);
      if (items) items.push(row);
      else map.set(row.offer_bk, [row]);
    });
    return map;
  };
  const facts = byOffer(tables.daily),
    markets = byOffer(tables.markets);
  const dimensions = new Map(tables.dimensions.map((r) => [r.offer_bk, r]));
  const identities = new Map(tables.identities.map((r) => [r.offer_bk, r]));
  for (const c of campaigns) {
    c.status = c.endDate < asOf ? 'completed' : 'live';
    c.isMature = c.endDate <= cutoff;
    c.durationDays = Core.days(c.startDate, c.endDate);
    c.observationEnd = [c.endDate, cutoff].sort()[0];
    c.daysObserved = Math.max(0, Core.days(c.startDate, c.observationEnd));
    c.daily = (facts.get(c.offerBk) || [])
      .filter((r) => r.date >= c.startDate && r.date <= c.observationEnd)
      .map(
        (r) =>
          ({
            date: r.date,
            impressions: r.impressions,
            views: r.views,
            packages: r.packages,
            margin: r.margin,
            impressionObserved: r.impression_observed,
            viewsObserved: r.views_observed,
            salesObserved: r.sales_observed,
          }) as FlashDaily,
      )
      .sort((a, b) => a.date.localeCompare(b.date));
    for (const r of markets.get(c.offerBk) || []) {
      if (r.date < c.startDate || r.date > c.observationEnd) continue;
      const market = r.market === 'UNKNOWN' ? 'Unknown' : r.market;
      (c.dailyByMarket![market] ||= []).push({
        date: r.date,
        views: r.views,
        packages: r.packages,
        margin: r.margin,
        viewsObserved: r.views_observed,
        salesObserved: r.sales_observed,
      });
    }
    const dim = dimensions.get(c.offerBk);
    c.pcmName = one(dim?.pcm_values, 'No current-quarter PCM record', 'Multiple PCM mappings');
    c.strategicTerritory = one(
      dim?.territory_values,
      'Unknown strategic territory',
      'Conflicting strategic territories',
    );
    c.ownerSalesforce = one(dim?.offer_owner_values, '', '') || null;
    const ids: string[] = identities.get(c.offerBk)?.identity_values || [];
    c.propertyIds = ids;
    c.propertyIdentityStatus = ids.length === 1 ? 'unique' : ids.length ? 'ambiguous' : 'missing';
    c.propertyId = ids.length === 1 ? ids[0] : null;
    const measured = Core.measure(c, Core.DEFAULTS, cutoff);
    c.totals = measured.totals;
    c.metrics = measured.metrics;
  }
  const supported = (field: string) => ({ supported: true, field });
  return {
    meta: {
      snapshotVersion: 2,
      asOf,
      generatedAt: new Date().toISOString(),
      observationCutoff: cutoff,
      commonFactDate: Object.values(freshness).sort()[0],
      factFreshness: freshness,
      population: 'Canonical hotel Flash campaigns from 1 January 2026 · live warehouse queries',
      dimensionTiming: 'Current offer metadata; PCM uses the current quarter sales tracker.',
      caveats: [
        'Today is excluded. All sources use a common completed fact date.',
        'No source records means missing coverage, not proven zero demand.',
        'Adjusted margin is before refunds; all transaction types are retained.',
        'Historical facts and schedules can revise; compare identical windows when reconciling.',
        'Market filtering withholds both impression metrics; search impressions have no website-market dimension.',
      ],
    },
    metricDefinitions: [
      ['impressions_per_day', 'Impressions / day', 'number'],
      ['views_per_day', 'Offer views / day', 'number'],
      ['impressions_to_views', 'Impressions → views', 'percent'],
      ['packages_per_day', 'Packages / day', 'number'],
      ['views_to_packages', 'Views → packages', 'percent'],
      ['margin_per_day', 'Adjusted margin / day', 'currency'],
      ['margin_per_view', 'Adjusted margin / view', 'currency'],
    ].map(([id, label, unit]) => ({ id, label, unit })),
    sourceCapabilities: {
      product: {
        supported: true,
        field: 'productBk',
        requiredValue: 'hotel',
        source: 'datawarehouse.dim_offer.product_bk',
        definition: 'Canonical linked bookable product, failing closed on unverified aliases.',
      },
      country: supported('country'),
      destination: supported('destination'),
      owner: supported('owner'),
      campaignType: supported('campaignType'),
      launchDate: supported('startDate'),
      observationDate: supported('date'),
      campaignAge: supported('age'),
      search: supported('name'),
      market: {
        supported: true,
        partial: true,
        values: [...new Set(tables.markets.map((r) => (r.market === 'UNKNOWN' ? 'Unknown' : r.market)))].sort(),
        unavailableMetrics: ['impressions_per_day', 'impressions_to_views'],
        reason: 'Search impressions have no website-market dimension.',
      },
      pcm: { ...supported('pcmName'), timing: 'Quarter sales attribution' },
      territory: { ...supported('strategicTerritory'), timing: 'Current Salesforce commercial territory' },
      propertyIdentity: {
        ...supported('propertyId'),
        historyStartDate: '2026-01-01',
        definition: 'Accommodation master identity with reservation fallback; ambiguous identities retained.',
      },
    },
    campaigns: campaigns.filter((c) => c.daysObserved > 0),
    cohorts: {},
  };
}
